import React from 'react';
import { Card, CardContent } from '@mui/material';
import { findMarketMatch, findFetchMatch } from '../utils/portfolioUtils.js';
import { toIndianRupeeFormat } from '../custom.js';


const getDisplayQty = (order) => {
    const derivatives = ["EQUITY", "EQUITY(INDEX)"];

    if (derivatives.includes(order.exchange)) {
        return { label: "Qty", value: order.qty };
    }
    return { label: "Lots", value: order.lots ?? order.qty };
};

const getDisplayQtySquareOff = (order) => {
    const derivatives = ["EQUITY", "EQUITY(INDEX)"];

    if (derivatives.includes(order.exchange)) {
        return { labelSquareOff: "Qty", valueSquareOff: order.qty_difference };
    }
    return { labelSquareOff: "Lots", valueSquareOff: order.lot_difference ?? order.qty_difference };
};

function OrderCard({ activeTab, order, fetchData, onClick }) {

    const match = findMarketMatch(fetchData, order.token);

    const fetchMatch = findFetchMatch(fetchData, order.token);

    const getChangeColor = (change) => {
        if (change > 0) return "text-green-500 w-16";
        if (change < 0) return "text-red-500 w-16";
        return "w-16";
    };

    const getAvgPriceColor = (avgPrice, orderPrice) => {
        if (avgPrice > orderPrice) return "text-green-500";
        if (avgPrice < orderPrice) return "text-red-500";
        return "";
    };
    const { label, value } = getDisplayQty(order);

    const { labelSquareOff, valueSquareOff } = getDisplayQtySquareOff(order);

    const sidePrice =
        order.type === "sell"
            ? match?.depth?.sell?.[0]?.price
            : match?.depth?.buy?.[0]?.price;

    const quantity = order.qty || order.lots || 0;

    // Cost basis must be the AVERAGE price of the aggregated position, not the
    // last order's price. On a first buy these are equal; on a second buy of
    // the same script `order.price` is only the latest fill, which made the P/L
    // wrong. Values can arrive as comma-formatted strings, so strip separators.
    const toNum = (v) => {
        if (typeof v === "number") return v;
        const n = parseFloat(String(v ?? "").replace(/,/g, ""));
        return isNaN(n) ? null : n;
    };
    const costBasis = toNum(order.avarage_price) ?? toNum(order.price);

    const cmdDifference =
        sidePrice && costBasis != null
            ? (
                order.type === "buy"
                    ? (sidePrice - costBasis)
                    : (costBasis - sidePrice)
            ) * quantity
            : null;

    return activeTab !== 'square_off' ? (
        <Card
            onClick={() => onClick(match)}
            className="px-0 py-1 mt-2 h-auto bg-white cursor-pointer hover:shadow-md transition-shadow"
        >
            <CardContent className="p-2">
                <div className="flex justify-between items-center w-full">
                    {/* Left side */}
                    <div className="flex flex-col items-start w-1/2 gap-y-1">
                        <span className="font-semibold text-[12px] text-gray-500">
                            <span className='bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300'>{order.exchange}</span>

                            {label}: <span className="text-black">{value}</span>
                        </span>
                        <span className="font-semibold text-[14px]">
                            {order.symbol}
                        </span>
                        <span className="font-semibold text-[12px] text-gray-500">
                            Last Order Price: <span className="text-black">₹ {toIndianRupeeFormat(order.price)}</span>
                        </span>
                        <span className="font-semibold text-[12px] text-gray-500">
                            P/L:
                            <span className={getChangeColor(cmdDifference)}>
                                {/* &nbsp; {toIndianRupeeFormat(sidePrice?.toFixed(2))} */}
                                &nbsp; {cmdDifference !== null
                                    ? `${cmdDifference > 0 ? "+" : "-"}${toIndianRupeeFormat(Math.abs(cmdDifference))}`
                                    : "N/A"}
                            </span>
                        </span>
                    </div>

                    {/* Right side */}
                    <div className="flex flex-col items-end w-1/2 gap-y-1">
                        <div>
                            <span
                                className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-full
                                    ${order.type === "buy" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" : ""}
                                    ${order.type === "sell" ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300" : ""}
                                `}
                            >
                                {order?.type.toUpperCase()}
                            </span>
                            <span className="font-semibold text-[12px] text-gray-500">
                                LTP: <span className="text-black">
                                    {match ? `₹ ${match.ltp}` : "N/A"}
                                </span>
                            </span>
                        </div>
                        <span className="font-semibold text-[12px] text-gray-500">
                            Change: <span className={getChangeColor(match?.netChange)}>
                                ₹{match ? match.netChange : "N/A"}
                            </span>
                        </span>
                        <span className="font-semibold text-[12px] text-gray-500">
                            Brokerage: <span className="text-black">
                                ₹{toIndianRupeeFormat(order.brokerage)}
                            </span>
                        </span>
                        <span className="font-semibold text-[12px] text-gray-500">
                            Avg: <span>
                                {toIndianRupeeFormat(order?.avarage_price ?? "N/A")}
                            </span>
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card >
    ) : (
        <Card className="px-0 py-1 mt-2 h-auto bg-white cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-2">
                <div className="">
                    {/* Left side */}
                    <div className="flex flex-row justify-between gap-y-1">
                        {/* <span className="font-semibold text-[12px] text-gray-500">
                            <span className='bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300'>{order.exchange}</span>
                            Qty: <span className="text-black">{order.qty_difference}</span>
                        </span> */}
                        <span className="font-semibold text-[14px]">
                            {order.symbol}
                        </span>

                        <div className='flex items-end flex-col gap-2 w-40'>
                            <span className="font-semibold text-[12px] text-gray-600 flex" >
                                <div>
                                    Buy :
                                </div>
                                &nbsp;
                                <div className="text-green-500 w-16">
                                    {toIndianRupeeFormat(order.buy) ?? "N/A"}
                                </div>
                            </span>

                            <span className="font-semibold text-[12px] text-gray-600 flex">
                                <div>
                                    Sell :
                                </div>
                                &nbsp;
                                <div className="text-red-500 w-16">
                                    {toIndianRupeeFormat(order.sell) ?? "N/A"}
                                </div>
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-row justify-between gap-y-1">
                        <span className="font-semibold text-[12px] text-gray-500">
                            <span className='bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300'>{order.exchange}</span>
                            {labelSquareOff}: <span className="text-black">{valueSquareOff}</span>
                        </span>
                        {/* <span className="font-semibold text-[14px]">
                            {order.symbol}
                            </span> */}
                        <span className="font-semibold text-[12px] text-gray-500 flex">
                            <div>
                                Brokerage :
                            </div>
                            &nbsp;
                            <div className="text-black w-16">
                                {toIndianRupeeFormat(order.total_brokerage) ?? "N/A"}
                            </div>
                        </span>
                    </div>
                    <div className="flex flex-row justify-between gap-y-1">
                        <span className="font-semibold text-[12px] text-gray-500">
                            Invested: <span className="text-black">₹ {toIndianRupeeFormat(order.invested_price)}</span>
                        </span>
                        <span className="font-semibold text-[12px] text-gray-500 flex">
                            <div>
                                P&L :{" "}
                            </div>
                            &nbsp;
                            <div className={getChangeColor(order.profit_loss)}>
                                {order.profit_loss !== undefined && order.profit_loss !== null ? (
                                    <>
                                        {order.profit_loss > 0 ? "+" : order.profit_loss < 0 ? "-" : ""}
                                        {toIndianRupeeFormat(Math.abs(order.profit_loss))}
                                    </>
                                ) : (
                                    "N/A"
                                )}
                            </div>
                        </span>

                    </div>
                    <div className="flex flex-row justify-between gap-y-1">
                        <span className="font-semibold text-[12px] text-gray-500">
                            Selling Price: <span className={getAvgPriceColor(fetchMatch?.avgPrice, order.price)}>
                                <span className="text-black">
                                    ₹{toIndianRupeeFormat(order.current_price) ?? "N/A"}
                                </span>
                                {/* ({fetchMatch ? fetchMatch.percentChange : "N/A"}) */}
                            </span>
                        </span>
                        <span className="font-semibold text-[12px] text-gray-500 flex">
                            <div>
                                Net Total :{" "}
                            </div>
                            <div className={getChangeColor(order.net_total)}>
                                {order.net_total !== undefined && order.net_total !== null ? (
                                    <>
                                        {order.net_total > 0 ? "+" : order.net_total < 0 ? "-" : ""}
                                        {toIndianRupeeFormat(Math.abs(order.net_total))}
                                    </>
                                ) : (
                                    "N/A"
                                )}
                            </div>
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card >
    )


}

export default OrderCard;